
import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, Loader2, HelpCircle, AlertCircle, Coffee, ChevronRight, Eye, EyeOff, Database, Check } from 'lucide-react';
import { db } from '../services/db';

interface LoginViewProps {
  onSuccess: () => void;
}

export const LoginView: React.FC<LoginViewProps> = ({ onSuccess }) => {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberPass, setRememberPass] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isDbError, setIsDbError] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Load remembered password on mount
  useEffect(() => {
    const saved = db.getLocalPassword();
    if (saved) {
      setPassword(saved);
      setRememberPass(true);
    }
    
    const focusTimer = setTimeout(() => {
      inputRef.current?.focus();
    }, 500);
    return () => clearTimeout(focusTimer);
  }, []);

  const toHalfWidth = (str: string) => {
    return str.replace(/[０-９]/g, (s) => {
      return String.fromCharCode(s.charCodeAt(0) - 0xFEE0);
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = toHalfWidth(e.target.value);
    setError('');
    setIsDbError(false);
    setPassword(val);
  };

  const handleLogin = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (isLoading || password.length === 0) return;
    
    setIsLoading(true);
    setError('');
    setIsDbError(false);
    
    try {
      await db.login(password);
      
      // Handle password remembering
      if (rememberPass) {
        db.saveLocalPassword(password);
      } else {
        db.clearLocalPassword();
      }

      onSuccess();
    } catch (e: any) {
      setIsLoading(false);
      const errorMsg = e.message || '';
      
      if (errorMsg.includes('D1') || errorMsg.includes('Internal Server Error') || errorMsg.includes('table') || errorMsg.includes('Fetch failed')) {
        setError('データベース未接続または初期設定未完了');
        setIsDbError(true);
      } else {
        setError(errorMsg || 'パスワードが違います');
        setPassword('');
        setIsDbError(false);
        setTimeout(() => {
          inputRef.current?.focus();
        }, 10);
      }
    }
  };

  const handleReset = async () => {
    if (confirm('パスワードをリセットしますか？不明な場合はシステム管理者に連絡してください。')) {
      setIsLoading(true);
      setError('');
      setIsDbError(false);
      try {
        await db.resetPassword();
        alert('パスワードをリセットしました。');
        setPassword('');
        db.clearLocalPassword();
        inputRef.current?.focus();
      } catch (e: any) {
        if (e.message?.includes('D1') || e.message?.includes('table')) {
          setError('データベース未接続 (リセット不可)');
          setIsDbError(true);
        } else {
          setError('リセットに失敗しました。');
        }
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="h-screen bg-stone-50 flex flex-col items-center justify-center p-6 relative overflow-hidden">
      <div className="absolute inset-0 z-0">
        <div className="absolute top-[-20%] left-[-10%] w-[70%] h-[70%] bg-orange-100/40 rounded-full blur-[120px] animate-pulse" />
        <div className="absolute bottom-[-20%] right-[-10%] w-[80%] h-[80%] bg-stone-200/60 rounded-full blur-[140px]" />
        <div className="absolute top-[30%] right-[10%] w-[40%] h-[40%] bg-rose-50/50 rounded-full blur-[100px]" />
      </div>

      <motion.div 
        initial={{ opacity: 0, y: 30 }}
        animate={(error && !isDbError) ? 
          { x: [-10, 10, -10, 10, 0], opacity: 1, y: 0 } : 
          { opacity: 1, y: 0 }
        }
        transition={{ 
          type: "spring", 
          damping: 25, 
          stiffness: 120,
          x: { duration: 0.4 }
        }}
        className="w-full max-w-md bg-white/60 backdrop-blur-2xl rounded-[3rem] shadow-[0_32px_64px_-16px_rgba(0,0,0,0.1)] p-10 border border-white/50 z-10 flex flex-col items-center"
      >
        <div className="mb-10 text-center">
          <motion.div 
            initial={{ scale: 0.8, rotate: -10 }}
            animate={{ scale: 1, rotate: 0 }}
            className="w-20 h-20 bg-stone-800 text-white rounded-[2rem] flex items-center justify-center mx-auto mb-6 shadow-2xl relative"
          >
             <Lock size={32} />
             <div className="absolute -top-2 -right-2 w-8 h-8 bg-orange-400 rounded-full flex items-center justify-center border-4 border-white">
                <Coffee size={14} className="text-white" />
             </div>
          </motion.div>
          <h1 className="text-3xl font-black text-stone-800 tracking-tighter mb-2">SpaceSimple</h1>
          <p className="text-stone-500 text-sm font-bold tracking-tight">管理システムへログイン</p>
        </div>

        <form onSubmit={handleLogin} className="w-full space-y-6">
          <div className="relative group">
            <label className="text-[10px] font-black text-stone-400 ml-5 mb-2 block uppercase tracking-[0.2em]">Password</label>
            <div className="relative flex items-center">
              <input 
                ref={inputRef}
                type={showPassword ? "text" : "password"}
                inputMode="numeric"
                required
                disabled={isLoading}
                autoComplete="current-password"
                className={`w-full bg-white/80 border-2 rounded-2xl px-12 py-4 text-center text-2xl font-black tracking-[0.5em] text-stone-800 shadow-sm transition-all focus:shadow-xl outline-none placeholder:text-stone-200 placeholder:tracking-normal ${error ? 'border-red-300' : 'border-transparent focus:border-stone-800'} disabled:opacity-50`}
                placeholder="••••"
                value={password}
                onChange={handleInputChange}
              />
              
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 p-2 text-stone-400 hover:text-stone-600 transition-colors"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <div className="flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={() => setRememberPass(!rememberPass)}
              className="flex items-center gap-2 group cursor-pointer"
            >
              <div className={`w-5 h-5 rounded-md border-2 transition-all flex items-center justify-center ${rememberPass ? 'bg-orange-400 border-orange-400' : 'border-stone-200 bg-white'}`}>
                {rememberPass && <Check size={14} className="text-white" />}
              </div>
              <span className="text-xs font-bold text-stone-400 group-hover:text-stone-600 transition-colors">パスワードを記憶する</span>
            </button>
          </div>

          <AnimatePresence>
            {error && (
              <motion.div 
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className={`flex items-center gap-3 text-red-500 text-xs font-bold p-4 rounded-2xl border ${isDbError ? 'bg-amber-50 border-amber-200 text-amber-700' : 'bg-red-50 border-red-100'}`}
              >
                {isDbError ? <Database size={16} className="shrink-0" /> : <AlertCircle size={16} className="shrink-0" />}
                <div className="flex flex-col">
                  <span>{error}</span>
                  {isDbError && <span className="text-[10px] font-medium opacity-70 leading-tight mt-1">D1データベースでSQL「実行」が完了しているか確認してください</span>}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <button 
            type="submit"
            disabled={isLoading || password.length === 0}
            className="w-full bg-stone-800 text-white font-black py-5 rounded-2xl shadow-xl hover:bg-stone-700 active:scale-95 transition-all flex items-center justify-center gap-3 disabled:opacity-50 group"
          >
            {isLoading ? (
              <Loader2 className="animate-spin" size={24} />
            ) : (
              <>
                <span>ログイン</span>
                <ChevronRight size={20} className="group-hover:translate-x-1 transition-transform" />
              </>
            )}
          </button>
        </form>

        <div className="mt-12 w-full flex flex-col items-center gap-4">
          <button 
            type="button"
            disabled={isLoading}
            onClick={handleReset} 
            className="text-[11px] font-black text-stone-400 hover:text-orange-500 transition-colors uppercase tracking-widest flex items-center gap-1 group disabled:opacity-30"
          >
            <HelpCircle size={14} className="opacity-50 group-hover:opacity-100" />
            パスワードを忘れましたか？
          </button>
          
          <div className="h-[1px] w-12 bg-stone-200" />
          
          <p className="text-[10px] text-stone-400 font-bold opacity-40 uppercase tracking-widest">
            SpaceSimple v2.5.5
          </p>
        </div>
      </motion.div>
    </div>
  );
};
